import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import {
  getBlogCategories,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
} from "JS/API";
import { BLOG_SECTION_OPTIONS } from "utils/blogConstants";

const SECTIONS = BLOG_SECTION_OPTIONS.filter((o) => o.value !== "all");
const KEY_REGEX = /^[a-z0-9_]{1,20}$/;
const EMPTY_NEW = { key: "", name: "", sort_order: 0 };

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
  },
  closeBtn: { border: "none", background: "transparent", cursor: "pointer", color: "#64748b", padding: "6px" },
  tabs: { display: "flex", gap: "6px", padding: "12px 22px 0" },
  tab: { padding: "6px 14px", borderRadius: "999px", border: "1px solid #d1d5db", background: "#ffffff", fontSize: "13px", cursor: "pointer", fontFamily: "inherit", color: "#374151" },
  tabActive: { background: "#1e293b", color: "#ffffff", borderColor: "#1e293b" },
  hint: { padding: "10px 22px 4px", fontSize: "12px", color: "#64748b" },
  list: { flex: 1, overflowY: "auto" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 60px 70px auto",
    gap: "10px",
    alignItems: "center",
    padding: "10px 22px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "13px",
  },
  key: { color: "#64748b", fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all" },
  input: { border: "1px solid #d1d5db", borderRadius: "6px", padding: "5px 8px", fontSize: "13px", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  hidden: { fontSize: "11px", color: "#9ca3af", marginLeft: "6px", fontWeight: "400" },
  toggle: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#374151", cursor: "pointer" },
  actions: { display: "flex", gap: "6px" },
  btn: { padding: "5px 10px", borderRadius: "6px", border: "1px solid #d1d5db", background: "#ffffff", fontSize: "12px", cursor: "pointer", fontFamily: "inherit", color: "#374151", whiteSpace: "nowrap" },
  btnPrimary: { background: "#2563eb", color: "#ffffff", borderColor: "#2563eb" },
  btnDanger: { background: "#fef2f2", color: "#991b1b", borderColor: "transparent" },
  addRow: { display: "grid", gridTemplateColumns: "1fr 1.2fr 60px auto", gap: "8px", alignItems: "center", padding: "14px 22px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0" },
  error: { padding: "6px 22px 0", fontSize: "12px", color: "#dc2626" },
  loading: { padding: "40px", textAlign: "center", color: "#9ca3af", fontSize: "13px" },
};

const BlogCategoryManageModal = ({ onClose, onChanged }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [section, setSection] = useState("blog");
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", sort_order: 0 });
  const [newCategory, setNewCategory] = useState(EMPTY_NEW);
  const [error, setError] = useState("");
  const [changed, setChanged] = useState(false);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const load = useCallback(async () => {
    try {
      const result = await getBlogCategories();
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setCategories(unwrapResponse(result) || []);
    } catch (e) {
      catchError(e, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const finish = useCallback(() => {
    if (changed) onChanged?.();
    onClose();
  }, [changed, onChanged, onClose]);

  const withResult = async (call) => {
    setError("");
    try {
      const result = await call();
      if (typeof result !== "object" || !result?.data) {
        catchError(result, navigate, setAlertBox, setAlert);
        return null;
      }
      setChanged(true);
      await load();
      return unwrapResponse(result);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (err?.response?.status === 422) {
        setError("입력 형식이 올바르지 않습니다.");
      } else {
        catchError(err, navigate, setAlertBox, setAlert);
      }
      return null;
    }
  };

  const changeSection = (value) => {
    setSection(value);
    setEditingId(null);
    setError("");
  };

  const handleAdd = async () => {
    const key = newCategory.key.trim();
    const name = newCategory.name.trim();
    if (!KEY_REGEX.test(key)) {
      setError("key는 영문 소문자·숫자·밑줄(_)로 20자 이내로 입력해 주세요.");
      return;
    }
    if (!name) {
      setError("카테고리 이름을 입력해 주세요.");
      return;
    }
    const created = await withResult(() =>
      createBlogCategory({ key, section, name, sort_order: Number(newCategory.sort_order) || 0, is_active: true })
    );
    if (created) setNewCategory(EMPTY_NEW);
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setDraft({ name: category.name, sort_order: category.sort_order });
    setError("");
  };

  const saveEdit = async (category) => {
    const name = draft.name.trim();
    if (!name) {
      setError("카테고리 이름을 입력해 주세요.");
      return;
    }
    const sortOrder = Number(draft.sort_order) || 0;
    const patch = {};
    if (name !== category.name) patch.name = name;
    if (sortOrder !== category.sort_order) patch.sort_order = sortOrder;
    if (Object.keys(patch).length === 0) {
      setEditingId(null);
      return;
    }
    const updated = await withResult(() => updateBlogCategory(category.id, patch));
    if (updated) setEditingId(null);
  };

  const toggleActive = (category) =>
    withResult(() => updateBlogCategory(category.id, { is_active: !category.is_active }));

  const handleDelete = async (category) => {
    if (!window.confirm(`"${category.name}" 카테고리를 삭제할까요?`)) return;
    await withResult(() => deleteBlogCategory(category.id));
  };

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const visible = categories.filter((c) => (c.section || "blog") === section);

  return (
    <>
      <div style={styles.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) finish(); }}>
        <div style={styles.modal} role="dialog" aria-label="카테고리 관리">
          <div style={styles.header}>
            <span>카테고리 관리</span>
            <button type="button" aria-label="닫기" style={styles.closeBtn} onClick={finish}><FaTimes size={16} /></button>
          </div>
          <div style={styles.tabs}>
            {SECTIONS.map((s) => (
              <button
                key={s.value}
                type="button"
                style={{ ...styles.tab, ...(section === s.value ? styles.tabActive : {}) }}
                onClick={() => changeSection(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div style={styles.hint}>
            순서가 작을수록 앞에 노출됩니다. key는 생성 후 바꿀 수 없습니다. 글이 있는 카테고리는 삭제할 수 없으니 노출을 끄세요.
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.list}>
            {isLoading ? (
              <div style={styles.loading}>로딩 중...</div>
            ) : visible.map((category) => {
              const editing = editingId === category.id;
              return (
                <div key={category.id} style={styles.row} data-testid={`category-row-${category.id}`}>
                  <span style={styles.key}>{category.key}</span>
                  {editing ? (
                    <input
                      aria-label={`${category.name} 이름`}
                      style={styles.input}
                      value={draft.name}
                      maxLength={50}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) saveEdit(category); if (e.key === "Escape") setEditingId(null); }}
                    />
                  ) : (
                    <span style={{ fontWeight: "600", color: category.is_active ? "#1f2937" : "#9ca3af" }}>
                      {category.name}
                      {!category.is_active && <span style={styles.hidden}>비노출</span>}
                    </span>
                  )}
                  {editing ? (
                    <input
                      type="number"
                      min={0}
                      aria-label={`${category.name} 순서`}
                      style={styles.input}
                      value={draft.sort_order}
                      onChange={(e) => setDraft((d) => ({ ...d, sort_order: e.target.value }))}
                    />
                  ) : (
                    <span style={{ color: "#64748b" }}>{category.sort_order}</span>
                  )}
                  <label style={styles.toggle}>
                    <input
                      type="checkbox"
                      aria-label={`${category.name} 노출`}
                      checked={!!category.is_active}
                      onChange={() => toggleActive(category)}
                    />
                    노출
                  </label>
                  <div style={styles.actions}>
                    {editing ? (
                      <>
                        <button type="button" style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => saveEdit(category)}>저장</button>
                        <button type="button" style={styles.btn} onClick={() => setEditingId(null)}>취소</button>
                      </>
                    ) : (
                      <>
                        <button type="button" style={styles.btn} onClick={() => startEdit(category)}>수정</button>
                        <button type="button" style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => handleDelete(category)}>삭제</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={styles.addRow}>
            <input
              aria-label="새 카테고리 key"
              style={styles.input}
              placeholder="key (예: trademark_case)"
              value={newCategory.key}
              maxLength={20}
              onChange={(e) => setNewCategory((n) => ({ ...n, key: e.target.value }))}
            />
            <input
              aria-label="새 카테고리 이름"
              style={styles.input}
              placeholder="이름"
              value={newCategory.name}
              maxLength={50}
              onChange={(e) => setNewCategory((n) => ({ ...n, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) handleAdd(); }}
            />
            <input
              type="number"
              min={0}
              aria-label="새 카테고리 순서"
              style={styles.input}
              value={newCategory.sort_order}
              onChange={(e) => setNewCategory((n) => ({ ...n, sort_order: e.target.value }))}
            />
            <button type="button" style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleAdd}>추가</button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </>
  );
};

export default BlogCategoryManageModal;
